import React from "react";
import { useUnengagedProof } from "../../../hooks/useUnengagedProof";

interface Props {
  organizationId: string;
  setterId: string | null;
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
  // Match SetterProofModal's onConversationClick signature exactly (4 args, last optional).
  // Source: components/dm/SetterProofModal.tsx interface SetterProofModalProps.
  onConversationClick: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null,
    highlightTimestamps?: { highlight: string; anchor?: string }
  ) => void;
}

export const UnengagedProofView: React.FC<Props> = ({
  organizationId,
  setterId,
  dateRange,
  onConversationClick,
}) => {
  const { rows, totalCount, loading, error } = useUnengagedProof({
    organizationId,
    setterId,
    dateRange,
    enabled: true,
    limit: 25,
    offset: 0,
  });

  if (loading && rows.length === 0) {
    return (
      <div className="text-glass-muted token-12 py-8 text-center">
        Loading unengaged conversations…
      </div>
    );
  }
  if (error) {
    return <div className="text-ds-red token-12 py-4">Error: {error}</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-glass-muted token-12 py-8 text-center">
        Nothing here — every assigned conversation in this date range got at least one setter reply.
      </div>
    );
  }

  return (
    <div>
      <p className="token-11 text-glass-secondary mb-3">
        {totalCount.toLocaleString()} ignored conversation{totalCount === 1 ? "" : "s"} · Lead
        reached out, no setter reply ever sent.
      </p>
      <div className="space-y-2">
        {rows.map((r) => (
          <button
            key={r.conversation_id}
            onClick={() => onConversationClick(r.conversation_id, r.contact_name, r.lead_instagram)}
            className="anim-tap w-full text-left p-3 rounded-lg bg-black/[0.02] hover:bg-black/[0.04] transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="token-13 text-glass font-medium truncate">{r.contact_name}</span>
              <span className="token-11 text-ds-red tabular-nums flex-shrink-0 ml-2">
                {r.days_since_lead_first_reached_out}d ignored
              </span>
            </div>
            {r.lead_instagram && (
              <p className="token-10 text-glass-muted mt-0.5">@{r.lead_instagram}</p>
            )}
            <p className="token-11 text-glass-secondary mt-1 line-clamp-2 italic">
              &ldquo;{r.last_message_text}&rdquo;
            </p>
          </button>
        ))}
      </div>
      {totalCount > rows.length && (
        <p className="token-11 text-glass-muted text-center mt-4">
          Showing {rows.length} of {totalCount.toLocaleString()} · pagination not implemented (P1)
        </p>
      )}
    </div>
  );
};
